import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';

/** 场景顺序（后台新增分类时需同步此处） */
export const PHRASE_SCENES = ['通用', '清明', '忌日', '生日', '节日'];

interface Phrase {
  id: string;
  content: string;
  scene: string;
}

/**
 * 追思常用语选择器：按场景切换，点击即填入目标输入框，并上报一次「选用」用于统计。
 * 用于「追思留言」与「祭品寄语」。
 */
export default function PhrasePicker({
  onPick,
  label = '选用常用语',
}: {
  onPick: (text: string) => void;
  label?: string;
}) {
  const [items, setItems] = useState<Phrase[]>([]);
  const [open, setOpen] = useState(false);
  const [scene, setScene] = useState<string>('通用');

  useEffect(() => {
    api<{ items: Phrase[] }>('/phrases').then((res) => {
      if (res.ok && res.data) setItems(res.data.items);
    });
  }, []);

  // 只显示实际有内容的场景
  const scenes = useMemo(() => {
    const has = new Set(items.map((i) => i.scene));
    const ordered = PHRASE_SCENES.filter((s) => has.has(s));
    // 后台自定义的其它场景兜底追加
    items.forEach((i) => {
      if (!ordered.includes(i.scene)) ordered.push(i.scene);
    });
    return ordered;
  }, [items]);

  const visible = useMemo(
    () => items.filter((i) => i.scene === scene),
    [items, scene]
  );

  if (items.length === 0) return null;

  function pick(p: Phrase) {
    onPick(p.content);
    setOpen(false);
    // 记录使用次数（失败也不影响选用）
    api(`/phrases/${p.id}/use`, { method: 'POST' });
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border border-[var(--c-border)] px-3 py-1 text-[11px] text-[var(--c-muted)] transition hover:border-[var(--c-accent)] hover:text-[var(--c-accent)]"
      >
        {open ? '收起常用语' : label}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)] p-2">
          {/* 场景切换 */}
          <div className="mb-2 flex flex-wrap gap-1">
            {scenes.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScene(s)}
                className={`rounded-full px-2.5 py-0.5 text-[10px] transition ${
                  scene === s
                    ? 'bg-[var(--c-accent)] text-white'
                    : 'bg-[var(--c-accent-soft)] text-[var(--c-text)]'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* 该场景下的常用语 */}
          <div className="max-h-44 space-y-1 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-2 py-1 text-[11px] text-[var(--c-muted)]">
                该场景下暂无常用语
              </p>
            ) : (
              visible.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pick(p)}
                  className="block w-full rounded px-2 py-1.5 text-left text-xs leading-relaxed text-[var(--c-text)] transition hover:bg-[var(--c-accent-soft)]"
                >
                  {p.content}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
